import { load } from '@fingerprintjs/botd';
import { ApiClient, type ApiClientOptions, type ChildGuideItem } from '@tunein/web-api-client';
import { Singleton, type SingletonInstance } from '@tunein/web-utils';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { reportingAdSlotNames } from 'src/common/constants/reporting/adSlot';
import { adTypes } from 'src/common/constants/reporting/adTypes';
import { player, playerClick } from 'src/common/constants/reporting/sandboxCategoryActionProps';
import { selectDiscordState } from 'src/common/selectors/app';
import { safeJsonStringify } from 'src/common/utils/object/safeJsonStringify';
import { getContentEventData } from 'src/common/utils/reporting/containerItems';

export class ApiBetaSingleton extends Singleton<ApiClient> {
  init(options: ApiClientOptions) {
    this.instance = new ApiClient(options);

    this.#reportBotDetection();
  }

  reportPlayerError(context: any) {
    const {
      playerName = '',
      streamId = '',
      url = '',
      format = '',
      error = {},
      originalErrorMessage = '',
      wasInSteadyState,
    } = context || {};

    apiBeta.telemetry.reportSandbox({
      category: player.category,
      action: player.actions.error,
      props: {
        playerName,
        streamId,
        url,
        format,
        errorData: safeJsonStringify(error.data) || '',
        errorMessage: error.message || originalErrorMessage || 'Unknown error',
        errorType: error.type || 'Unknown',
        isMediaError: error.isMediaError?.toString() || '',
        isNetworkError: error.isNetworkError?.toString() || '',
        wasInSteadyState: wasInSteadyState?.toString() || 'false',
      },
    });
  }

  reportPlayerStopClick() {
    const discordState = selectDiscordState(storeSingleton.state);

    if (!discordState.canControlPlayback) {
      return;
    }

    return apiBeta.telemetry.reportSandbox({
      category: playerClick.category,
      action: playerClick.actions.stop,
      props: {},
    });
  }

  reportPlayerPauseClick() {
    const discordState = selectDiscordState(storeSingleton.state);

    if (!discordState.canControlPlayback) {
      return;
    }

    apiBeta.telemetry.reportSandbox({
      category: playerClick.category,
      action: playerClick.actions.pause,
      props: {},
    });
  }

  reportContentImpression(guideItem: ChildGuideItem) {
    const event = getContentEventData({
      guideItem,
      state: storeSingleton.state,
    });

    apiBeta.telemetry.reportContentImpression(event);
  }

  reportContentClicked(guideItem: ChildGuideItem) {
    const event = getContentEventData({
      guideItem,
      state: storeSingleton.state,
      includeActionType: true,
    });

    // @ts-expect-error - TODO: verify potential schema validation errors
    apiBeta.telemetry.reportContentClicked(event);
  }

  // TODO: delete after Player Manager migration is complete. Handled internally in PM.
  reportAdEligiblity({ audio = false, video = false }) {
    apiBeta.telemetry.reportAdsVideoAudioRollEligibilityDecided({
      isEligible: false,
      isPlatformEligible: audio,
      // @ts-expect-error
      adType: adTypes.AD_TYPE_AUDIO,
      // @ts-expect-error
      adSlot: reportingAdSlotNames.AD_SLOT_PREROLL,
    });
    apiBeta.telemetry.reportAdsVideoAudioRollEligibilityDecided({
      isEligible: false,
      isPlatformEligible: video,
      // @ts-expect-error
      adType: adTypes.AD_TYPE_VIDEO,
      // @ts-expect-error
      adSlot: reportingAdSlotNames.AD_SLOT_PREROLL,
    });
  }

  async #reportBotDetection() {
    try {
      const botd = await load({ monitoring: false });
      const result = await botd.detect();

      this.instance.telemetry.reportSandbox({
        category: 'botd',
        action: 'detect',
        props: {
          isBot: String(result.bot),
        },
      });
    } catch (error) {
      this.instance.telemetry.logError({
        message: 'Bot detection or reporting failed',
        context: {
          error,
        },
      });
    }
  }
}

export const apiBeta = new ApiBetaSingleton() as SingletonInstance<ApiClient, ApiBetaSingleton>;
